import React, { useState, useEffect, useRef } from 'react';
import AddUser from './AddUser';
import { api } from '../config/api';
import './AddUser.css';

interface ExistingLot {
  lotId: string;
  displayLotId: string;
  companyName: string;
  address: string;
  lotName: string;
  lotCapacity: number;
  hasEvCharging: boolean;
  lotStatus: string;
  currentOccupancy: number;
  createdOn: string;
  owner: {
    userId: string;
    name: string;
    email: string;
  };
}

interface User {
  userId: string;
  name: string;
  email: string;
  role: string;
  displayUserId: string;
  isDeleted: boolean;
}

interface AddLotProps {
  existingLots: ExistingLot[];
  onClose: () => void;
  onLotAdded: () => void;
}

const AddLot: React.FC<AddLotProps> = ({ existingLots, onClose, onLotAdded }) => {
  const currentUserEmail = localStorage.getItem('userEmail') || '';
  const [showAddUser, setShowAddUser] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Lot data state WITHOUT lot ID (will be generated by backend only at creation time)
  const [lotData, setLotData] = useState({
    companyName: '',
    address: '',
    lotName: 'New Parking Lot',
    ownerId: '',
    lotCapacity: 0,
    hasEvCharging: false,
    createdBy: 'superadmin'
  });

  // Address components state
  const [streetNumber, setStreetNumber] = useState("");
  const [streetName, setStreetName] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("Canada");
  const [provinceState, setProvinceState] = useState("");
  const [postalCode, setPostalCode] = useState("");

  // Address validation state
  const [streetNumberError, setStreetNumberError] = useState(false);
  const [streetNameError, setStreetNameError] = useState(false);
  const [cityError, setCityError] = useState(false);
  const [provinceStateError, setProvinceStateError] = useState(false);
  const [postalCodeError, setPostalCodeError] = useState(false);

  // Canadian provinces
  const canadianProvinces = [
    { value: "AB", label: "Alberta" },
    { value: "BC", label: "British Columbia" },
    { value: "MB", label: "Manitoba" },
    { value: "NB", label: "New Brunswick" },
    { value: "NL", label: "Newfoundland and Labrador" },
    { value: "NS", label: "Nova Scotia" },
    { value: "NT", label: "Northwest Territories" },
    { value: "NU", label: "Nunavut" },
    { value: "ON", label: "Ontario" },
    { value: "PE", label: "Prince Edward Island" },
    { value: "QC", label: "Quebec" },
    { value: "SK", label: "Saskatchewan" },
    { value: "YT", label: "Yukon" }
  ];

  // US states
  const usStates = [
    { value: "AL", label: "Alabama" },
    { value: "AK", label: "Alaska" },
    { value: "AZ", label: "Arizona" },
    { value: "AR", label: "Arkansas" },
    { value: "CA", label: "California" },
    { value: "CO", label: "Colorado" },
    { value: "CT", label: "Connecticut" },
    { value: "DE", label: "Delaware" },
    { value: "DC", label: "District of Columbia" },
    { value: "FL", label: "Florida" },
    { value: "GA", label: "Georgia" },
    { value: "HI", label: "Hawaii" },
    { value: "ID", label: "Idaho" },
    { value: "IL", label: "Illinois" },
    { value: "IN", label: "Indiana" },
    { value: "IA", label: "Iowa" },
    { value: "KS", label: "Kansas" },
    { value: "KY", label: "Kentucky" },
    { value: "LA", label: "Louisiana" },
    { value: "ME", label: "Maine" },
    { value: "MD", label: "Maryland" },
    { value: "MA", label: "Massachusetts" },
    { value: "MI", label: "Michigan" },
    { value: "MN", label: "Minnesota" },
    { value: "MS", label: "Mississippi" },
    { value: "MO", label: "Missouri" },
    { value: "MT", label: "Montana" },
    { value: "NE", label: "Nebraska" },
    { value: "NV", label: "Nevada" },
    { value: "NH", label: "New Hampshire" },
    { value: "NJ", label: "New Jersey" },
    { value: "NM", label: "New Mexico" },
    { value: "NY", label: "New York" },
    { value: "NC", label: "North Carolina" },
    { value: "ND", label: "North Dakota" },
    { value: "OH", label: "Ohio" },
    { value: "OK", label: "Oklahoma" },
    { value: "OR", label: "Oregon" },
    { value: "PA", label: "Pennsylvania" },
    { value: "RI", label: "Rhode Island" },
    { value: "SC", label: "South Carolina" },
    { value: "SD", label: "South Dakota" },
    { value: "TN", label: "Tennessee" },
    { value: "TX", label: "Texas" },
    { value: "UT", label: "Utah" },
    { value: "VT", label: "Vermont" },
    { value: "VA", label: "Virginia" },
    { value: "WA", label: "Washington" },
    { value: "WV", label: "West Virginia" },
    { value: "WI", label: "Wisconsin" },
    { value: "WY", label: "Wyoming" }
  ];

  // Address validation functions
  const validateStreetNumber = (value: string): boolean => {
    if (value.trim().length === 0) return true; // Don't show error for empty fields
    return /^[0-9]+$/.test(value.trim()) && value.length <= 10;
  };

  const validateStreetName = (value: string): boolean => {
    if (value.trim().length === 0) return true; // Don't show error for empty fields
    return value.length <= 50;
  };

  const validateCity = (value: string): boolean => {
    if (value.trim().length === 0) return true; // Don't show error for empty fields
    return value.length <= 30;
  };

  const validatePostalCode = (value: string): boolean => {
    if (value.trim().length === 0) return true; // Don't show error for empty fields
    if (country === "Canada") {
      // Canadian postal code format: A1A 1A1
      return /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(value);
    } else {
      // US ZIP code format: 12345 or 12345-6789
      return /^\d{5}(-\d{4})?$/.test(value);
    }
  };

  // Handle address component changes
  const handleAddressComponentChange = (component: string, value: string) => {
    let updatedStreetNumber = streetNumber;
    let updatedStreetName = streetName;
    let updatedCity = city;
    let updatedCountry = country;
    let updatedProvinceState = provinceState;
    let updatedPostalCode = postalCode;

    switch (component) {
      case 'streetNumber':
        // Only allow numbers
        const numericValue = value.replace(/[^0-9]/g, '');
        updatedStreetNumber = numericValue;
        setStreetNumber(numericValue);
        setStreetNumberError(!validateStreetNumber(numericValue));
        break;
      case 'streetName':
        updatedStreetName = value;
        setStreetName(value);
        setStreetNameError(!validateStreetName(value));
        break;
      case 'city':
        updatedCity = value;
        setCity(value);
        setCityError(!validateCity(value));
        break;
      case 'country':
        updatedCountry = value;
        updatedProvinceState = ""; // Reset province/state when country changes
        setCountry(value);
        setProvinceState("");
        break;
      case 'provinceState':
        updatedProvinceState = value;
        setProvinceState(value);
        setProvinceStateError(!value);
        break;
      case 'postalCode':
        let formattedValue = value;
        if (updatedCountry === "Canada") {
          // Format Canadian postal code (remove spaces/hyphens, then add space)
          formattedValue = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
          if (formattedValue.length > 3) {
            formattedValue = formattedValue.substring(0, 3) + ' ' + formattedValue.substring(3, 6);
          }
        } else {
          // US ZIP code - allow numbers and hyphens
          formattedValue = value.replace(/[^0-9-]/g, '');
        }
        updatedPostalCode = formattedValue;
        setPostalCode(formattedValue);
        setPostalCodeError(!validatePostalCode(formattedValue));
        break;
    }

    // Update the combined address
    const combinedAddress = [
      updatedStreetNumber.trim(),
      updatedStreetName.trim(),
      updatedCity.trim(),
      updatedProvinceState,
      updatedPostalCode.trim(),
      updatedCountry
    ].join(', ');

    setLotData(prev => ({
      ...prev,
      address: combinedAddress
    }));
  };

  // Fetch users on mount
  useEffect(() => {
    fetchUsers();
  }, []);

  // Filter users based on search term
  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    const formattedUserId = user.displayUserId.replace('PWP-U-', '');

    return (
      user.name.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower) ||
      formattedUserId.toLowerCase().includes(searchLower) ||
      user.displayUserId.toLowerCase().includes(searchLower)
    );
  });

  // Get selected user display info
  const getSelectedUserDisplay = () => {
    if (!lotData.ownerId) return 'Select an owner...';
    const selectedUser = users.find(user => user.userId === lotData.ownerId);
    if (!selectedUser) return 'Select an owner...';

    const formattedUserId = selectedUser.displayUserId.replace('PWP-U-', '');
    return `${formattedUserId} (${selectedUser.email}) ${selectedUser.name}`;
  };

  const handleUserSelect = (userId: string) => {
    setLotData({ ...lotData, ownerId: userId });
    setIsDropdownOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  // Add keyboard event handlers for ESC and Enter keys
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !showAddUser) {
        // Only close if the AddUser modal is not open
        if (isDropdownOpen) {
          setIsDropdownOpen(false);
          setHighlightedIndex(-1);
        } else {
          onClose();
        }
      } else if (e.key === "Enter") {
        // Only trigger submit if not typing in an input
        const target = e.target as HTMLElement;
        if (target.tagName !== "INPUT" && target.tagName !== "SELECT" && target.tagName !== "TEXTAREA" && !showAddUser) {
          if (isDropdownOpen && highlightedIndex >= 0) {
            e.preventDefault();
            const selectedUser = filteredUsers[highlightedIndex];
            if (selectedUser) {
              handleUserSelect(selectedUser.userId);
            }
          } else {
            handleSubmit();
          }
        }
      } else if (isDropdownOpen && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
        e.preventDefault();
        const currentIndex = highlightedIndex;
        let newIndex = currentIndex;

        if (e.key === "ArrowDown") {
          newIndex = currentIndex < filteredUsers.length - 1 ? currentIndex + 1 : 0;
        } else if (e.key === "ArrowUp") {
          newIndex = currentIndex > 0 ? currentIndex - 1 : filteredUsers.length - 1;
        }

        setHighlightedIndex(newIndex);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, showAddUser, lotData, isDropdownOpen, highlightedIndex, filteredUsers]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Reset highlighted index when search term changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchTerm]);

  // Fetch users from backend
  const fetchUsers = async () => {
    try {
      const response = await api.get('/users/get-all-users');
      const data = response.data;
      if (data.success) {
        setUsers(data.data);
      } else {
        console.error('Failed to fetch users:', data.message);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Submit new lot to the backend
  const handleSubmit = async () => {
    // Validate required fields
    if (!lotData.companyName || !lotData.ownerId ||
      !streetNumber.trim() || !streetName.trim() || !city.trim() ||
      !provinceState || !postalCode.trim()) {
      alert('Please fill all required fields: Company Name, complete Address, and Owner.');
      return;
    }

    // Check for validation errors
    if (streetNumberError || streetNameError || cityError || provinceStateError || postalCodeError) {
      alert('Please fix the address validation errors before submitting.');
      return;
    }

    try {
      const response = await api.post('/parkinglots/create-parking-lot', {
        ownerId: lotData.ownerId,
        companyName: lotData.companyName,
        address: lotData.address,
        lotName: lotData.lotName,
        lotCapacity: parseInt(lotData.lotCapacity.toString()),
        hasEvCharging: false,
        createdBy: 'SUPERADMIN_USER_ID'
      });

      const data = response.data;

      if (data.success) {
        console.log('Parking lot created successfully:', data.data);
        onLotAdded();
      } else {
        console.error('Failed to create parking lot:', data.message);
        alert('Error creating lot:\n' + data.message || 'Failed to create parking lot');
      }
    } catch (error: any) {
      console.error('Error creating parking lot:', error);
      let errorMessage = 'Failed to create parking lot';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      alert(errorMessage);
    }
  };

  // Handle user creation from AddUser modal
  const handleUserCreated = (userId: string) => {
    setShowAddUser(false);
    fetchUsers(); // Refresh the users list
    // Set the newly created user as the selected owner
    setLotData({ ...lotData, ownerId: userId });
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>Add New Parking Lot</h2>

        <label>Company Name:</label>
        <input
          type="text"
          value={lotData.companyName}
          onChange={(e) => setLotData({ ...lotData, companyName: e.target.value })}
          placeholder='Enter the company name'
        />

        <label>Address:</label>
        <div className="address-grid">
          <div className="address-row">
            <div className="address-field">
              <label>Street Number:</label>
              <input
                type="text"
                className={`address-input ${streetNumberError ? 'invalid' : ''}`}
                value={streetNumber}
                maxLength={10}
                onChange={(e) => handleAddressComponentChange('streetNumber', e.target.value)}
                placeholder="123"
              />
            </div>
            <div className="address-field">
              <label>Street Name:</label>
              <input
                type="text"
                className={`address-input ${streetNameError ? 'invalid' : ''}`}
                value={streetName}
                maxLength={50}
                onChange={(e) => handleAddressComponentChange('streetName', e.target.value)}
                placeholder="Main Street"
              />
            </div>
            <div className="address-field">
              <label>City:</label>
              <input
                type="text"
                className={`address-input ${cityError ? 'invalid' : ''}`}
                value={city}
                maxLength={30}
                onChange={(e) => handleAddressComponentChange('city', e.target.value)}
                placeholder="Toronto"
              />
            </div>
          </div>

          <div className="address-row">
            <div className="address-field">
              <label>Country:</label>
              <select
                className="address-dropdown"
                value={country}
                onChange={(e) => handleAddressComponentChange('country', e.target.value)}
              >
                <option value="Canada">Canada</option>
                <option value="United States">United States</option>
              </select>
            </div>
            <div className="address-field">
              <label>{country === "Canada" ? "Province:" : "State:"}</label>
              <select
                className={`address-dropdown ${provinceStateError ? 'invalid' : ''}`}
                value={provinceState}
                onChange={(e) => handleAddressComponentChange('provinceState', e.target.value)}
              >
                <option value="">Select {country === "Canada" ? "Province" : "State"}</option>
                {country === "Canada"
                  ? canadianProvinces.map((province) => (
                    <option key={province.value} value={province.value}>{province.label}</option>
                  ))
                  : usStates.map((state) => (
                    <option key={state.value} value={state.value}>{state.label}</option>
                  ))
                }
              </select>
            </div>
            <div className="address-field">
              <label>Postal Code:</label>
              <input
                type="text"
                className={`address-input ${postalCodeError ? 'invalid' : ''}`}
                value={postalCode}
                maxLength={country === "Canada" ? 7 : 10}
                onChange={(e) => handleAddressComponentChange('postalCode', e.target.value)}
                placeholder={country === "Canada" ? "A1A 1A1" : "12345"}
              />
            </div>
          </div>
        </div>

        {/* Show address errors */}
        {(streetNumberError || streetNameError || cityError || provinceStateError || postalCodeError) && (
          <div className="address-errors">
            {streetNumberError && streetNumber.trim().length > 0 && (
              <p className="error">Street number must contain only numbers (max 10 characters)</p>
            )}
            {streetNameError && streetName.trim().length > 0 && (
              <p className="error">Street name must be 50 characters or less</p>
            )}
            {cityError && city.trim().length > 0 && (
              <p className="error">City must be 30 characters or less</p>
            )}
            {provinceStateError && (
              <p className="error">Please select a {country === "Canada" ? "province" : "state"}</p>
            )}
            {postalCodeError && postalCode.trim().length > 0 && (
              <p className="error">
                {country === "Canada"
                  ? "Invalid Canadian postal code format (e.g., A1A 1A1)"
                  : "Invalid ZIP code format (e.g., 12345 or 12345-6789)"
                }
              </p>
            )}
          </div>
        )}

        <div className="lot-info-row">
          <div className="lot-info-field">
            <label>Lot Name:</label>
            <input
              type="text"
              className="lot-info-input"
              value={lotData.lotName}
              onChange={(e) => setLotData({ ...lotData, lotName: e.target.value })}
              placeholder='Enter the lot name'
            />
          </div>
          <div className="lot-info-field">
            <label>Capacity:</label>
            <input
              type="number"
              className="lot-info-input"
              value={lotData.lotCapacity}
              onChange={(e) => setLotData({ ...lotData, lotCapacity: Number(e.target.value) })}
              min="0"
              placeholder="0"
            />
          </div>
        </div>

        <div className="owner-email-section">
          <label>Owner:</label>
          <button className="addlot-new-user-button" onClick={() => setShowAddUser(true)}>New User</button>
        </div>
        <div className="select-container">
          <div className={`custom-select-container ${isDropdownOpen ? 'open' : ''}`} ref={dropdownRef}>
            <div
              className="select-display"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <span className="selected-text">{getSelectedUserDisplay()}</span>
              <span className="select-arrow">▼</span>
            </div>

            {isDropdownOpen && (
              <div className="select-dropdown">
                <div className="search-input-container">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by name, email, or ID..."
                    className="search-input"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>

                <div className="dropdown-options">
                  {filteredUsers.length === 0 ? (
                    <div className="no-results">No users found</div>
                  ) : (
                    filteredUsers.map((user, index) => {
                      const formattedUserId = user.displayUserId.replace('PWP-U-', '');
                      return (
                        <div
                          key={user.userId}
                          className={`dropdown-option ${lotData.ownerId === user.userId ? 'selected' : ''} ${highlightedIndex === index ? 'highlighted' : ''}`}
                          onClick={() => handleUserSelect(user.userId)}
                          onMouseEnter={() => setHighlightedIndex(index)}
                        >
                          <div className="user-option-info">
                            <div className="user-option-id">{formattedUserId}</div>
                            <div className="user-option-email">{user.email}</div>
                            <div className="user-option-name">{user.name}</div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="button-group" style={{ marginTop: '20px' }}>
          <button className="confirm-button" onClick={handleSubmit} style={{ marginRight: '10px' }}>
            Save
          </button>
          <button className="cancel-button" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>

      {showAddUser && (
        <AddUser
          isOpen={showAddUser}
          onClose={() => setShowAddUser(false)}
          onConfirm={handleUserCreated}
          type="owner"
          currentOwnerId=""
          currentOperators={[]}
          currentStaff={[]}
          startInCreateMode={true}
        />
      )}
    </div>
  );
};

export default AddLot;
